'use client';
import React from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const values = [
  {
    icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
    title: 'Integrity',
    description: 'We operate with unwavering honesty and transparency in every client and candidate interaction.',
  },
  {
    icon: 'M13 10V3L4 14h7v7l9-11h-7z',
    title: 'Excellence',
    description: 'We set the highest standards for talent quality, service delivery, and client outcomes.',
  },
  {
    icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',
    title: 'Partnership',
    description: 'We build lasting relationships, acting as a true extension of your talent acquisition team.',
  },
  {
    icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z',
    title: 'Innovation',
    description: 'We continuously evolve our processes and technology to deliver faster, smarter talent solutions.',
  },
];

const team = [
  {
    name: 'Michael Thompson',
    title: 'Chief Executive Officer',
    bio: '25+ years in IT staffing and consulting. Former CTO at a Fortune 100 technology firm.',
    initials: 'MT',
  },
  {
    name: 'Jennifer Park',
    title: 'Chief Operating Officer',
    bio: '18 years leading talent operations for global staffing organizations across North America.',
    initials: 'JP',
  },
  {
    name: 'David Okafor',
    title: 'VP of Client Success',
    bio: 'Dedicated to building long-term partnerships with Fortune 500 technology leaders.',
    initials: 'DO',
  },
  {
    name: 'Rachel Kim',
    title: 'Director of Talent Acquisition',
    bio: 'Expert in sourcing and placing senior IT professionals across cloud, AI, and cybersecurity.',
    initials: 'RK',
  },
];

const milestones = [
  { year: '2004', event: 'TranceformRIGHT founded in Dallas, TX with a focus on IT contract staffing.' },
  { year: '2008', event: 'Expanded to serve Fortune 500 clients nationally, opening offices in New York and Chicago.' },
  { year: '2012', event: 'Launched executive search practice, placing C-suite technology leaders at top enterprises.' },
  { year: '2016', event: 'Reached 5,000 successful placements milestone. Recognized as Top IT Staffing Firm.' },
  { year: '2019', event: 'Expanded service portfolio to include IT consulting and digital transformation advisory.' },
  { year: '2024', event: 'Celebrating 20 years of excellence with 15,000+ placements and 500+ active Fortune 500 clients.' },
];

const coreServices = [
  'IT Staffing', 'IT Consulting', 'Recruitment Process Outsourcing', 'MSP', 'VMS Staffing',
  'Accounting and Finance Staffing', 'PMO', '1099 Compliance', '1099 Validation',
  'Global Payroll', 'High Hazard Payroll', 'Curated Talent Cloud', 'Employer of Record',
];

export default function AboutPage() {
  return (
    <div className="min-h-screen" style={{ background: '#ffffff' }}>
      <Navbar />

      {/* Page Hero - Pinnacle1 style: dark navy bg, thin Poppins H1 */}
      <section
        className="relative overflow-hidden"
        style={{
          paddingTop: '144px',
          paddingBottom: '80px',
          background: 'linear-gradient(135deg, #01142D 0%, #0A1F3D 60%, #1F65AB 100%)',
        }}
      >
        {/* Background monogram */}
        <div
          className="absolute right-0 bottom-0 select-none pointer-events-none"
          style={{
            fontSize: '320px',
            fontFamily: 'Poppins, sans-serif',
            fontWeight: 700,
            color: 'rgba(0,167,255,0.05)',
            lineHeight: 1,
          }}
        >
          AU
        </div>
        <div className="container-custom relative z-10">
          <div className="flex items-center gap-2 mb-4">
            <Link
              href="/"
              style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', color: 'rgba(255,255,255,0.5)' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#ffffff'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.5)'; }}
            >
              Home
            </Link>
            <span style={{ color: 'rgba(255,255,255,0.3)' }}>/</span>
            <span style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', color: '#00A7FF' }}>About Us</span>
          </div>
          <h1
            className="mb-4"
            style={{
              fontFamily: 'Poppins, sans-serif',
              fontSize: 'clamp(36px, 5vw, 64px)',
              fontWeight: 200,
              color: '#ffffff',
              lineHeight: 1.2,
            }}
          >
            About TranceformRIGHT
          </h1>
          <div style={{ width: '60px', height: '3px', background: '#00A7FF', marginBottom: '24px' }}></div>
          <p
            className="max-w-2xl"
            style={{ fontFamily: 'Poppins, sans-serif', fontSize: '18px', color: 'rgba(255,255,255,0.75)', lineHeight: 1.7 }}
          >
            For over 20 years, we’ve been the trusted IT staffing and consulting partner for Fortune 500 companies — connecting exceptional technology talent with organizations that shape the future.
          </p>
        </div>
      </section>

      {/* Mission & Story Section - Pinnacle1 style: white bg, alternating layout */}
      <section className="section-padding" style={{ background: '#ffffff' }}>
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h6
                className="mb-3"
                style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', fontWeight: 600, color: '#1F65AB', letterSpacing: '0.08em', textTransform: 'uppercase' }}
              >
                Our Story
              </h6>
              <h2
                className="mb-4"
                style={{ fontFamily: 'Poppins, sans-serif', fontSize: '36px', fontWeight: 100, color: 'rgba(0,0,0,0.7)', lineHeight: 1.3 }}
              >
                Transforming IT Talent Acquisition Since 2004
              </h2>
              <div style={{ width: '48px', height: '3px', background: '#00A7FF', marginBottom: '24px' }}></div>
              <p
                className="mb-5"
                style={{ fontFamily: 'Poppins, sans-serif', fontSize: '16px', color: '#48494B', lineHeight: 1.7 }}
              >
                TranceformRight is a leading provider of information technology, Recruitment Process Outsourcing, and workforce solutions, Healthcare Staffing. TransformRight is one of the best in information technology staffing and consulting services which provides staffing, MSP and payrolling services in the United States of America.
              </p>
              <p
                className="mb-6"
                style={{ fontFamily: 'Poppins, sans-serif', fontSize: '16px', color: '#48494B', lineHeight: 1.7 }}
              >
                TranceformRight has become one of the largest providers in its industry, with over 1000 professionals providing services across the U.S. and Canada. For more information visit{' '}
                <a href="http://www.tranceformright.com" target="_blank" rel="noopener noreferrer" style={{ color: '#00A7FF' }}>www.tranceformright.com</a>.
              </p>
              <p
                className="mb-6"
                style={{ fontFamily: 'Poppins, sans-serif', fontSize: '15px', fontWeight: 500, color: 'rgba(0,0,0,0.7)' }}
              >
                Our Core Services:
              </p>
              <div className="flex flex-wrap gap-2">
                {coreServices?.map((service, i) => (
                  <span
                    key={service}
                    className="px-3 py-1.5 text-sm"
                    style={{
                      background: i % 2 === 0 ? 'rgba(0,167,255,0.1)' : 'rgba(1,20,45,0.07)',
                      color: i % 2 === 0 ? '#0091DD' : '#01142D',
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: '13px',
                      border: `1px solid ${i % 2 === 0 ? 'rgba(0,167,255,0.2)' : 'rgba(1,20,45,0.15)'}`,
                    }}
                  >
                    {service}
                  </span>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-5">
              {[
                { value: '2004', label: 'Founded', sub: 'Started in Dallas, TX with a mission to transform IT staffing', dark: false },
                { value: '500+', label: 'Fortune 500', sub: 'Active enterprise clients across all major industries', dark: true },
                { value: '15K+', label: 'Placements', sub: 'Successful IT professionals placed in career-defining roles', dark: true },
                { value: '98%', label: 'Satisfaction', sub: 'Client satisfaction rate across all engagements', dark: false },
              ]?.map((stat, i) => (
                <div
                  key={stat?.label}
                  className={`p-6 ${i % 2 === 1 ? 'mt-6' : ''}`}
                  style={{
                    background: stat.dark ? '#01142D' : '#00A7FF',
                  }}
                >
                  <div
                    style={{
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: '40px',
                      fontWeight: 200,
                      color: '#ffffff',
                      lineHeight: 1,
                      marginBottom: '8px',
                    }}
                  >
                    {stat?.value}
                  </div>
                  <div
                    style={{
                      fontFamily: 'Montserrat, sans-serif',
                      fontSize: '13px',
                      fontWeight: 600,
                      color: 'rgba(255,255,255,0.9)',
                      letterSpacing: '0.05em',
                      textTransform: 'uppercase',
                      marginBottom: '8px',
                    }}
                  >
                    {stat?.label}
                  </div>
                  <div
                    style={{ fontFamily: 'Poppins, sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.65)', lineHeight: 1.5 }}
                  >
                    {stat?.sub}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision - Pinnacle1 style: light gray bg */}
      <section className="section-padding" style={{ background: '#FAFAFA' }}>
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-8">
            <div
              className="p-10 relative overflow-hidden"
              style={{ background: '#ffffff', border: '1px solid #e8e8e8', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}
            >
              <div
                className="absolute top-0 left-0 w-full h-1"
                style={{ background: '#00A7FF' }}
              />
              <div
                className="w-14 h-14 flex items-center justify-center mb-6"
                style={{ background: '#00A7FF' }}
              >
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </div>
              <h3
                className="mb-4"
                style={{ fontFamily: 'Poppins, sans-serif', fontSize: '24px', fontWeight: 400, color: 'rgba(0,0,0,0.8)' }}
              >
                Our Mission
              </h3>
              <p
                style={{ fontFamily: 'Poppins, sans-serif', fontSize: '16px', color: '#48494B', lineHeight: 1.7 }}
              >
                To empower Fortune 500 organizations with the RIGHT IT talent — delivering speed, precision, and quality in every placement to drive digital transformation and business growth.
              </p>
            </div>

            <div
              className="p-10 relative overflow-hidden"
              style={{ background: '#01142D', border: '1px solid rgba(0,167,255,0.2)' }}
            >
              <div
                className="absolute top-0 left-0 w-full h-1"
                style={{ background: '#00A7FF' }}
              />
              <div
                className="w-14 h-14 flex items-center justify-center mb-6"
                style={{ background: 'rgba(0,167,255,0.2)', border: '1px solid rgba(0,167,255,0.3)' }}
              >
                <svg className="w-7 h-7" style={{ color: '#00A7FF' }} fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
              </div>
              <h3
                className="mb-4"
                style={{ fontFamily: 'Poppins, sans-serif', fontSize: '24px', fontWeight: 400, color: '#ffffff' }}
              >
                Our Vision
              </h3>
              <p
                style={{ fontFamily: 'Poppins, sans-serif', fontSize: '16px', color: 'rgba(255,255,255,0.7)', lineHeight: 1.7 }}
              >
                To be the most trusted IT staffing and consulting partner in America — recognized for transforming organizations through exceptional talent, innovative solutions, and unwavering commitment to client success.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values - Pinnacle1 style: white bg */}
      <section className="section-padding" style={{ background: '#ffffff' }}>
        <div className="container-custom">
          <div className="text-center mb-12">
            <h6
              className="mb-3"
              style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', fontWeight: 600, color: '#1F65AB', letterSpacing: '0.1em', textTransform: 'uppercase' }}
            >
              What We Stand For
            </h6>
            <h2
              className="mb-4"
              style={{ fontFamily: 'Poppins, sans-serif', fontSize: '40px', fontWeight: 100, color: 'rgba(0,0,0,0.6)' }}
            >
              Our Core Values
            </h2>
            <div style={{ width: '48px', height: '3px', background: '#00A7FF', margin: '0 auto' }}></div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values?.map((value, i) => (
              <div
                key={value?.title}
                className="p-8 text-center group transition-all duration-300"
                style={{
                  background: '#ffffff',
                  border: '1px solid #e8e8e8',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.boxShadow = '0 8px 32px rgba(0,145,221,0.15)';
                  (e.currentTarget as HTMLElement).style.borderColor = 'rgba(0,167,255,0.3)';
                  (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 12px rgba(0,0,0,0.06)';
                  (e.currentTarget as HTMLElement).style.borderColor = '#e8e8e8';
                  (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
                }}
              >
                <div
                  className="w-16 h-16 flex items-center justify-center mx-auto mb-5 group-hover:scale-110 transition-transform duration-300"
                  style={{ background: i % 2 === 0 ? '#00A7FF' : '#01142D' }}
                >
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d={value?.icon} />
                  </svg>
                </div>
                <h3
                  className="mb-3"
                  style={{ fontFamily: 'Poppins, sans-serif', fontSize: '20px', fontWeight: 400, color: 'rgba(0,0,0,0.8)' }}
                >
                  {value?.title}
                </h3>
                <p
                  style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', color: '#48494B', lineHeight: 1.6 }}
                >
                  {value?.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline - Pinnacle1 style */}
      <section className="section-padding" style={{ background: '#FAFAFA' }}>
        <div className="container-custom">
          <div className="text-center mb-12">
            <h6
              className="mb-3"
              style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', fontWeight: 600, color: '#1F65AB', letterSpacing: '0.1em', textTransform: 'uppercase' }}
            >
              Our Journey
            </h6>
            <h2
              className="mb-4"
              style={{ fontFamily: 'Poppins, sans-serif', fontSize: '40px', fontWeight: 100, color: 'rgba(0,0,0,0.6)' }}
            >
              20 Years of Growth &amp; Excellence
            </h2>
            <div style={{ width: '48px', height: '3px', background: '#00A7FF', margin: '0 auto' }}></div>
          </div>
          <div className="relative">
            <div
              className="absolute left-1/2 -translate-x-0.5 top-0 bottom-0 w-0.5 hidden md:block"
              style={{ background: '#00A7FF', opacity: 0.3 }}
            />
            <div className="space-y-8">
              {milestones?.map((milestone, i) => (
                <div
                  key={milestone?.year}
                  className={`flex flex-col md:flex-row items-center gap-6 md:gap-10 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                >
                  <div className={`flex-1 ${i % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                    <div
                      className="inline-block max-w-sm p-6"
                      style={{
                        background: '#ffffff',
                        border: '1px solid #e8e8e8',
                        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                        borderTop: `3px solid ${i % 2 === 0 ? '#00A7FF' : '#01142D'}`,
                      }}
                    >
                      <div
                        className="mb-2"
                        style={{
                          fontFamily: 'Poppins, sans-serif',
                          fontSize: '24px',
                          fontWeight: 200,
                          color: i % 2 === 0 ? '#00A7FF' : '#01142D',
                        }}
                      >
                        {milestone?.year}
                      </div>
                      <p
                        style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', color: '#48494B', lineHeight: 1.6 }}
                      >
                        {milestone?.event}
                      </p>
                    </div>
                  </div>
                  <div
                    className="relative z-10 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: i % 2 === 0 ? '#00A7FF' : '#01142D' }}
                  >
                    <div className="w-3 h-3 rounded-full bg-white"></div>
                  </div>
                  <div className="flex-1 hidden md:block"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Leadership Banner - Pinnacle1 style: dark overlay image */}
      <section className="relative w-full overflow-hidden" style={{ height: '480px' }}>
        <img
          src="/assets/images/executives-discussion-bg.png"
          alt="Business executives and managers engaged in a strategic discussion around a conference table in a modern corporate office"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0" style={{ background: 'rgba(1,20,45,0.82)' }}></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
          <h6
            className="mb-4"
            style={{
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '12px',
              fontWeight: 600,
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              color: '#00A7FF',
            }}
          >
            Our Leadership
          </h6>
          <h2
            className="mb-4 max-w-2xl"
            style={{ fontFamily: 'Poppins, sans-serif', fontSize: '36px', fontWeight: 200, color: '#ffffff', lineHeight: 1.3 }}
          >
            Experienced Leaders, Proven Results
          </h2>
          <div style={{ width: '48px', height: '3px', background: '#00A7FF', marginBottom: '20px' }}></div>
          <p
            className="max-w-xl"
            style={{ fontFamily: 'Poppins, sans-serif', fontSize: '18px', color: 'rgba(255,255,255,0.75)', lineHeight: 1.7 }}
          >
            Our executive team combines deep industry knowledge with a passion for connecting exceptional talent with world-class organizations.
          </p>
        </div>
      </section>

      {/* Leadership Team - Pinnacle1 style: white bg cards */}
      <section className="section-padding" style={{ background: '#ffffff' }}>
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2
              className="mb-4"
              style={{ fontFamily: 'Poppins, sans-serif', fontSize: '40px', fontWeight: 100, color: 'rgba(0,0,0,0.6)' }}
            >
              Our Leadership Team
            </h2>
            <div style={{ width: '48px', height: '3px', background: '#00A7FF', margin: '0 auto' }}></div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {team?.map((member, i) => (
              <div
                key={member?.name}
                className="group transition-all duration-300"
                style={{
                  background: '#ffffff',
                  border: '1px solid #e8e8e8',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.boxShadow = '0 8px 32px rgba(0,145,221,0.15)';
                  (e.currentTarget as HTMLElement).style.borderColor = 'rgba(0,167,255,0.3)';
                  (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 12px rgba(0,0,0,0.06)';
                  (e.currentTarget as HTMLElement).style.borderColor = '#e8e8e8';
                  (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
                }}
              >
                <div
                  className="w-full flex items-center justify-center"
                  style={{
                    height: '160px',
                    background: i % 2 === 0 ? '#00A7FF' : '#01142D',
                  }}
                >
                  <span
                    style={{
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: '48px',
                      fontWeight: 200,
                      color: 'rgba(255,255,255,0.9)',
                    }}
                  >
                    {member?.initials}
                  </span>
                </div>
                <div className="p-6">
                  <h3
                    className="mb-1"
                    style={{ fontFamily: 'Poppins, sans-serif', fontSize: '18px', fontWeight: 400, color: 'rgba(0,0,0,0.8)' }}
                  >
                    {member?.name}
                  </h3>
                  <p
                    className="mb-3"
                    style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '12px', fontWeight: 600, color: '#00A7FF', letterSpacing: '0.05em', textTransform: 'uppercase' }}
                  >
                    {member?.title}
                  </p>
                  <p
                    style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', color: '#48494B', lineHeight: 1.6 }}
                  >
                    {member?.bio}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        className="py-20"
        style={{ background: 'linear-gradient(135deg, #01142D 0%, #0A1F3D 100%)' }}
      >
        <div className="container-custom text-center">
          <h2
            className="mb-4"
            style={{ fontFamily: 'Poppins, sans-serif', fontSize: '40px', fontWeight: 200, color: '#ffffff', lineHeight: 1.3 }}
          >
            Ready to Partner with TranceformRIGHT?
          </h2>
          <div style={{ width: '48px', height: '3px', background: '#00A7FF', margin: '0 auto 24px' }}></div>
          <p
            className="mb-8 max-w-xl mx-auto"
            style={{ fontFamily: 'Poppins, sans-serif', fontSize: '18px', color: 'rgba(255,255,255,0.7)', lineHeight: 1.7 }}
          >
            Let’s discuss how our 20 years of IT staffing expertise can help your organization achieve its technology goals.
          </p>
          <Link
            href="/contact"
            className="inline-flex items-center justify-center px-10 py-4 font-semibold transition-all duration-300 hover:opacity-90"
            style={{ background: '#00A7FF', color: '#ffffff', fontFamily: 'Montserrat, sans-serif', fontSize: '15px' }}
          >
            Get In Touch
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
